/*
 * cart-bar.js — sticky mobile "Add to cart" bar.
 *
 * NOTE (DeksTech): the original theme version added to cart by faking a click on
 * the real submit button, which relied on a native form submission being
 * intercepted. On mobile that was unreliable and could navigate to the cart PAGE.
 *
 * It was then changed to dispatch the theme's `theme:cart:add` event — but that
 * routes through the theme's addToCart(), which contains:
 *     this.cart ? (open drawer) : (window.location = routes.cart_url)
 * i.e. if `this.cart` is ever falsy (init timing, or a 3rd-party app re-rendering
 * the cart node) it REDIRECTS to /cart. That was still happening on mobile.
 *
 * This version does the add itself (POST /cart/add.js) and then asks the theme to
 * re-render the cart, which opens the drawer via the theme's own
 * `theme:product:added` chain. There is NO window.location anywhere in this file,
 * so the sticky bar can never open the cart page. Quantity + variant are taken
 * straight from the product form, so the quantity selector is honored, and a guard
 * ensures one tap = one add.
 */
(function () {
  "use strict";

  class CartBar extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest("product-info");
      this.form = this.section.querySelector("[data-product-form]");
      this.formWrapper =
        (theme.settings.productPageSticky &&
          this.section.querySelector("[data-form-wrapper]")) ||
        this.form;

      this.handleClick = this.handleClick.bind(this);
      this.boundToggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);
      this.boundSetCartBarHeight = this.setCartBarHeight.bind(this);

      // Re-entrancy guard so a single tap can only fire one add.
      this.isAdding = false;
    }

    connectedCallback() {
      this.setCartBarHeight();

      // Bind once. removeEventListener with the same bound reference makes this
      // idempotent even if the element is reconnected, so listeners can't stack.
      this.removeEventListener("click", this.handleClick);
      this.addEventListener("click", this.handleClick);

      document.addEventListener("theme:scroll", this.boundToggleCartBarOnScroll);
      document.addEventListener("theme:resize", this.boundSetCartBarHeight);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      document.removeEventListener("theme:scroll", this.boundToggleCartBarOnScroll);
      document.removeEventListener("theme:resize", this.boundSetCartBarHeight);
    }

    handleClick(event) {
      const addButton = event.target.closest("[data-cart-bar-add-to-cart]");
      if (addButton) {
        event.preventDefault();
        this.addToCart(addButton);
        return;
      }

      if (event.target.closest("[data-cart-bar-scroll]")) {
        event.preventDefault();
        this.scrollToTop();
      }
    }

    addToCart(addButton) {
      if (this.isAdding) return;

      const realButton = this.form.querySelector("[data-add-to-cart]");
      if (!realButton || realButton.hasAttribute("disabled")) return;

      this.isAdding = true;
      addButton.classList.add("is-loading");
      addButton.setAttribute("disabled", "disabled");

      // Add directly via the AJAX cart API. FormData is built from the product
      // form, so [name="id"] (variant) and [name="quantity"] are included.
      fetch(window.theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/javascript",
        },
        body: new FormData(this.form),
      })
        .then((response) => response.json())
        .then((data) => {
          // Shopify returns a `status` field only on errors (sold out, limit, etc.)
          if (data && data.status) {
            this.onError(data, realButton);
            return;
          }

          // Re-render the cart. The theme responds to this by dispatching
          // `theme:product:added`, which opens the cart drawer. No navigation.
          document.dispatchEvent(
            new CustomEvent("theme:cart:refresh", { bubbles: true })
          );
        })
        .catch((error) => {
          this.onError(error, realButton);
        })
        .finally(() => {
          this.resetAddButton();
        });
    }

    onError(response, realButton) {
      // Let the theme show its standard cart error message (limit/sold out).
      document.dispatchEvent(
        new CustomEvent("theme:product:add-error", {
          detail: { response: response, button: realButton },
          bubbles: true,
        })
      );
      // Bring the form back into view so the shopper sees the error / options.
      this.scrollToTop();
    }

    resetAddButton() {
      const addButton = this.querySelector("[data-cart-bar-add-to-cart]");
      if (addButton) {
        addButton.classList.remove("is-loading");
        addButton.removeAttribute("disabled");
      }
      this.isAdding = false;
    }

    scrollToTop() {
      const variantSelects = this.section.querySelector("variant-selects");
      const target = window.theme.isMobile()
        ? variantSelects || this.form
        : this.section;
      const top = target.getBoundingClientRect().top;
      window.theme.scrollTo(window.theme.isMobile() ? top - 10 : top);
    }

    toggleCartBarOnScroll() {
      const scrollY = window.scrollY;
      if (this.formWrapper) {
        const isVisible =
          scrollY > this.formWrapper.offsetTop + this.formWrapper.offsetHeight;
        this.classList.toggle("is-visible", isVisible);
      }
    }

    setCartBarHeight() {
      const height = this.offsetHeight;
      document.documentElement.style.setProperty("--cart-bar-height", `${height}px`);
    }
  }

  if (!customElements.get("cart-bar")) {
    customElements.define("cart-bar", CartBar);
  }
})();
